import type { DragonDossier, GeneratorOptions } from "../types";

const DATABASE_NAME = "tiamat-workbench";
const STORE_NAME = "state";
const DATABASE_VERSION = 1;

interface LocalState {
  dossier: DragonDossier;
  options: GeneratorOptions;
  savedAt: string;
}

function openDatabase(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DATABASE_NAME, DATABASE_VERSION);
    request.onupgradeneeded = () => {
      const database = request.result;
      if (!database.objectStoreNames.contains(STORE_NAME)) {
        database.createObjectStore(STORE_NAME);
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function saveLocalState(dossier: DragonDossier, options: GeneratorOptions): Promise<void> {
  if (!("indexedDB" in window)) return;
  const database = await openDatabase();
  await new Promise<void>((resolve, reject) => {
    const transaction = database.transaction(STORE_NAME, "readwrite");
    transaction.objectStore(STORE_NAME).put(
      {
        dossier,
        options,
        savedAt: new Date().toISOString()
      } satisfies LocalState,
      "current"
    );
    transaction.oncomplete = () => resolve();
    transaction.onerror = () => reject(transaction.error);
  });
  database.close();
}

export async function loadLocalState(): Promise<LocalState | null> {
  if (!("indexedDB" in window)) return null;
  const database = await openDatabase();
  const result = await new Promise<LocalState | null>((resolve, reject) => {
    const transaction = database.transaction(STORE_NAME, "readonly");
    const request = transaction.objectStore(STORE_NAME).get("current");
    request.onsuccess = () => resolve((request.result as LocalState | undefined) ?? null);
    request.onerror = () => reject(request.error);
  });
  database.close();
  return result;
}
